import React, { useState, useCallback } from 'react';
import { getContexts, createContext, getContext, getContextTimeline, getContextEntries, getContextTasks } from '../../api/contexts';
import useApi from '../../hooks/useApi';
import TopBar from '../layout/TopBar';
import LoadingState from '../shared/LoadingState';
import ErrorState from '../shared/ErrorState';
import Badge from '../shared/Badge';
import Modal from '../shared/Modal';
import { CONTEXT_COLORS, CONTEXT_TYPE_LABELS, CONTEXT_STATUS_LABELS } from '../../utils/constants';
import { formatDate, formatDateTime } from '../../utils/dates';

const STATUS_TABS = ['activo', 'pausado', 'cerrado'];

function ContextCard({ ctx, onClick }) {
  const color = ctx.color || '#6C8EFF';
  return (
    <div className="context-card" onClick={() => onClick(ctx.id)} style={{ cursor: 'pointer' }}>
      <div className="context-card__bar" style={{ background: color }} />
      <div className="context-card__body">
        <div className="context-card__header">
          <span className="context-card__name">{ctx.name}</span>
          <span className="context-card__status" style={{ color }}>●</span>
        </div>
        <div className="context-card__meta">
          {CONTEXT_TYPE_LABELS[ctx.type] || ctx.type}
          {ctx.end_date && ` · hasta ${formatDate(ctx.end_date)}`}
        </div>
        {ctx.objective && (
          <p className="context-card__objective">
            "{ctx.objective.slice(0, 100)}{ctx.objective.length > 100 ? '…' : ''}"
          </p>
        )}
        <div className="context-card__stats">
          <span>{ctx.entry_count} entradas</span>
          <span>{ctx.task_count} tareas</span>
          <span>{ctx.risk_count} riesgos</span>
          {parseInt(ctx.open_risk_count) > 0 && (
            <span style={{ color: '#FF6B6B' }}>⚠️ {ctx.open_risk_count} abiertos</span>
          )}
          {parseInt(ctx.pending_decision_count) > 0 && (
            <span style={{ color: '#FBBF24' }}>🎯 {ctx.pending_decision_count} pendiente{ctx.pending_decision_count > 1 ? 's' : ''}</span>
          )}
        </div>
        {ctx.last_activity && (
          <div className="context-card__activity">
            Última actividad: {formatDateTime(ctx.last_activity)}
          </div>
        )}
      </div>
    </div>
  );
}

function NewContextModal({ isOpen, onClose, onCreate }) {
  const [form, setForm] = useState({ name: '', type: 'tema', objective: '', status: 'activo', end_date: '', color: CONTEXT_COLORS[0] });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  function set(field, value) { setForm(f => ({ ...f, [field]: value })); }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name.trim()) { setError('El nombre es requerido'); return; }
    setSaving(true);
    setError(null);
    try {
      const body = { name: form.name.trim(), type: form.type, status: form.status, color: form.color };
      if (form.objective) body.objective = form.objective;
      if (form.end_date) body.end_date = form.end_date;
      const res = await createContext(body);
      onCreate(res.data);
      setForm({ name: '', type: 'tema', objective: '', status: 'activo', end_date: '', color: CONTEXT_COLORS[0] });
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Nuevo contexto" size="md">
      <form onSubmit={handleSubmit} className="form-stack">
        {error && <div className="chat-result chat-result--error">{error}</div>}
        <label className="form-label">Nombre
          <input className="filter-input" value={form.name} onChange={e => set('name', e.target.value)} placeholder="Ej: PAP Framework v2" maxLength={500} required />
        </label>
        <label className="form-label">Tipo
          <select className="filter-select" value={form.type} onChange={e => set('type', e.target.value)}>
            <option value="tema">Tema</option>
            <option value="proyecto">Proyecto</option>
          </select>
        </label>
        <label className="form-label">Objetivo
          <textarea className="filter-input" rows={3} value={form.objective} onChange={e => set('objective', e.target.value)} placeholder="Objetivo principal de este contexto…" />
        </label>
        {form.type === 'proyecto' && (
          <label className="form-label">Fecha fin
            <input type="date" className="filter-input" value={form.end_date} onChange={e => set('end_date', e.target.value)} />
          </label>
        )}
        <div className="form-label">Color
          <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
            {CONTEXT_COLORS.map(c => (
              <button
                key={c}
                type="button"
                style={{
                  width: 28, height: 28, borderRadius: '50%', background: c, border: form.color === c ? '3px solid white' : '2px solid transparent',
                  boxShadow: form.color === c ? `0 0 0 2px ${c}` : 'none', cursor: 'pointer',
                }}
                onClick={() => set('color', c)}
              />
            ))}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
          <button type="button" className="btn-secondary" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Creando…' : 'Crear contexto'}</button>
        </div>
      </form>
    </Modal>
  );
}

function ContextTimeline({ contextId }) {
  const apiFn = useCallback(() => getContextTimeline(contextId), [contextId]);
  const { data, loading, error } = useApi(apiFn, [contextId]);

  const TYPE_ICONS = { entry: '📥', task_created: '✅', decision: '🎯', artifact: '📄', risk: '⚠️' };

  if (loading) return <LoadingState rows={4} />;
  if (error) return <ErrorState message={error} />;
  if (!data?.length) return <div className="empty-state">Sin actividad registrada</div>;

  return (
    <div className="timeline">
      {data.map(group => (
        <div key={group.date} className="timeline-group">
          <div className="timeline-date">{group.date}</div>
          {group.items.map((item, i) => (
            <div key={i} className="timeline-item">
              <span className="timeline-item__icon">{TYPE_ICONS[item.type] || '·'}</span>
              <span className="timeline-item__text">{item.title}</span>
              <span className="timeline-item__time" style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 'auto' }}>
                {item.timestamp ? new Date(item.timestamp).toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' }) : ''}
              </span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function ContextDetail({ contextId, onBack, showToast }) {
  const [activeTab, setActiveTab] = useState('resumen');
  const apiFn = useCallback(() => getContext(contextId), [contextId]);
  const { data, loading, error, refetch } = useApi(apiFn, [contextId]);

  if (loading) return <LoadingState rows={6} />;
  if (error) return <ErrorState message={error} onRetry={refetch} />;
  if (!data) return null;

  const color = data.color || '#6C8EFF';
  const TABS = ['resumen', 'timeline', 'entradas', 'tareas'];

  return (
    <div className="view-container">
      <TopBar
        title=""
        subtitle=""
      >
        <button className="btn-secondary" onClick={onBack} style={{ marginRight: 8 }}>← Contextos</button>
      </TopBar>

      <div className="view-content">
        <div className="context-detail__hero" style={{ borderLeft: `4px solid ${color}`, paddingLeft: 16, marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <h2 style={{ margin: 0, fontSize: 20 }}>{data.name}</h2>
              <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>
                {CONTEXT_TYPE_LABELS[data.type] || data.type} · {CONTEXT_STATUS_LABELS[data.status] || data.status}
                {data.start_date && ` · ${formatDate(data.start_date)}`}
                {data.end_date && ` → ${formatDate(data.end_date)}`}
              </div>
            </div>
            <Badge label={CONTEXT_STATUS_LABELS[data.status] || data.status} color={color} />
          </div>
          {data.objective && <p style={{ margin: '10px 0 0', fontSize: 14, color: 'var(--text-muted)' }}>"{data.objective}"</p>}
        </div>

        <div className="tabs-bar" style={{ marginBottom: 20 }}>
          {TABS.map(t => (
            <button
              key={t}
              className={`tab-btn${activeTab === t ? ' active' : ''}`}
              onClick={() => setActiveTab(t)}
              style={{ textTransform: 'capitalize' }}
            >
              {t === 'resumen' ? 'Resumen' : t === 'timeline' ? 'Timeline' : t === 'entradas' ? 'Entradas' : 'Tareas'}
            </button>
          ))}
        </div>

        {activeTab === 'resumen' && (
          <ContextResumen data={data} />
        )}
        {activeTab === 'timeline' && (
          <ContextTimeline contextId={contextId} />
        )}
        {activeTab === 'entradas' && (
          <ContextEntriesList contextId={contextId} />
        )}
        {activeTab === 'tareas' && (
          <ContextTasksList contextId={contextId} />
        )}
      </div>
    </div>
  );
}

function ContextResumen({ data }) {
  const s = data.summary || {};
  return (
    <div>
      <div className="stats-grid" style={{ marginBottom: 20 }}>
        <div className="stat-card"><div className="stat-card__value">{s.entry_count || 0}</div><div className="stat-card__label">ENTRADAS</div></div>
        <div className="stat-card"><div className="stat-card__value">{s.pending_task_count || 0}</div><div className="stat-card__label">TAREAS ACTIVAS</div></div>
        <div className="stat-card"><div className="stat-card__value">{s.open_risk_count || 0}</div><div className="stat-card__label">RIESGOS ABIERTOS</div></div>
        <div className="stat-card"><div className="stat-card__value">{s.pending_decision_count || 0}</div><div className="stat-card__label">DECISIONES PEND.</div></div>
      </div>

      {data.pending_tasks?.length > 0 && (
        <div className="card" style={{ marginBottom: 16 }}>
          <h3 className="card__title">Tareas pendientes</h3>
          {data.pending_tasks.map(t => (
            <div key={t.id} className="task-row">
              <span className="priority-dot" style={{ background: t.priority === 'critica' ? '#FF6B6B' : t.priority === 'alta' ? '#FBBF24' : '#6C8EFF' }} />
              <span>{t.title}</span>
              {t.due_date && <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 'auto' }}>{formatDate(t.due_date)}</span>}
            </div>
          ))}
        </div>
      )}

      {data.pending_decisions?.length > 0 && (
        <div className="card" style={{ marginBottom: 16 }}>
          <h3 className="card__title">Decisiones pendientes</h3>
          {data.pending_decisions.map(d => (
            <div key={d.id} style={{ padding: '8px 0', borderBottom: '1px solid var(--border)', fontSize: 14 }}>
              🎯 {d.question}
            </div>
          ))}
        </div>
      )}

      {data.open_risks?.length > 0 && (
        <div className="card">
          <h3 className="card__title">Riesgos abiertos</h3>
          {data.open_risks.map(r => (
            <div key={r.id} className="risk-card" style={{ marginBottom: 8 }}>
              <div className="risk-card__header">
                <span className="risk-dot" style={{ background: r.impact === 'alto' ? '#FF6B6B' : r.impact === 'medio' ? '#FBBF24' : '#4CAF82' }} />
                <span className="risk-card__desc">{r.description}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ContextEntriesList({ contextId }) {
  const apiFn = useCallback(() => getContextEntries(contextId, { limit: 30 }), [contextId]);
  const { data, loading, error } = useApi(apiFn, [contextId]);

  if (loading) return <LoadingState rows={4} />;
  if (error) return <ErrorState message={error} />;
  if (!data?.length) return <div className="empty-state">Sin entradas en este contexto</div>;

  return (
    <div>
      {data.map(e => (
        <div key={e.id} className="entry-row" style={{ padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>
            <span>{e.classification?.entry_type || 'sin tipo'}</span>
            <span>{formatDateTime(e.created_at)}</span>
          </div>
          <div style={{ fontSize: 14 }}>{e.content.slice(0, 120)}{e.content.length > 120 ? '…' : ''}</div>
        </div>
      ))}
    </div>
  );
}

function ContextTasksList({ contextId }) {
  const apiFn = useCallback(() => getContextTasks(contextId, { limit: 30 }), [contextId]);
  const { data, loading, error } = useApi(apiFn, [contextId]);
  const PRIORITY_COLORS = { critica: '#FF6B6B', alta: '#FBBF24', media: '#6C8EFF', baja: '#4CAF82' };

  if (loading) return <LoadingState rows={4} />;
  if (error) return <ErrorState message={error} />;
  if (!data?.length) return <div className="empty-state">Sin tareas activas en este contexto</div>;

  return (
    <div>
      {data.map(t => (
        <div key={t.id} className="task-row" style={{ padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
          <span className="priority-dot" style={{ background: PRIORITY_COLORS[t.priority] || '#6B7A99' }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14 }}>{t.title}</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{t.priority} · {t.status}</div>
          </div>
          {t.due_date && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{formatDate(t.due_date)}</span>}
        </div>
      ))}
    </div>
  );
}

function ContextsView({ showToast }) {
  const [statusTab, setStatusTab] = useState('activo');
  const [showNewModal, setShowNewModal] = useState(false);
  const [selectedContextId, setSelectedContextId] = useState(null);

  const apiFn = useCallback(() => getContexts({ status: statusTab, limit: 50 }), [statusTab]);
  const { data, loading, error, refetch } = useApi(apiFn, [statusTab]);

  if (selectedContextId) {
    return (
      <ContextDetail
        contextId={selectedContextId}
        onBack={() => setSelectedContextId(null)}
        showToast={showToast}
      />
    );
  }

  const total = data?.length || 0;

  return (
    <div className="view-container">
      <TopBar title="Contextos" subtitle={`${total} ${statusTab}s`}>
        <button className="btn-primary" onClick={() => setShowNewModal(true)}>+ Nuevo contexto</button>
      </TopBar>

      <div className="view-content">
        <div className="tabs-bar" style={{ marginBottom: 20 }}>
          {STATUS_TABS.map(tab => (
            <button
              key={tab}
              className={`tab-btn${statusTab === tab ? ' active' : ''}`}
              onClick={() => setStatusTab(tab)}
              style={{ textTransform: 'capitalize' }}
            >
              {CONTEXT_STATUS_LABELS[tab]}
            </button>
          ))}
        </div>

        {loading && <LoadingState rows={4} />}
        {error && <ErrorState message={error} onRetry={refetch} />}
        {!loading && !error && (
          data?.length ? (
            <div className="contexts-grid">
              {data.map(ctx => (
                <ContextCard key={ctx.id} ctx={ctx} onClick={setSelectedContextId} />
              ))}
            </div>
          ) : (
            <div className="empty-state">
              No hay contextos {statusTab}s.
              {statusTab === 'activo' && (
                <button className="btn-primary" style={{ marginTop: 12 }} onClick={() => setShowNewModal(true)}>
                  Crear el primero
                </button>
              )}
            </div>
          )
        )}
      </div>

      <NewContextModal
        isOpen={showNewModal}
        onClose={() => setShowNewModal(false)}
        onCreate={() => { refetch(); showToast?.('Contexto creado', 'success'); }}
      />
    </div>
  );
}

export default ContextsView;
